import { useTranslation } from 'react-i18next';

import { useIsAdmin } from '@/api/hooks/useAuth';
import { useBuildVersion } from '@/api/hooks/useVersion';

/** How much of the full SHA to show in the tooltip. Enough to be unambiguous,
 *  short enough to read. */
const SHORT_COMMIT_LENGTH = 8;

/** The build that is serving this page, for admins only.
 *
 *  Reuse notes: the admin gate mirrors `features/admin/models/JobsTray.tsx` --
 *  the exported wrapper does nothing but gate, so regular users never mount the
 *  query. Rendered as plain muted text rather than a Badge: this is metadata
 *  about the page, not a status, and the topbar is already pill-heavy.
 */
export function VersionChip() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return null;
  return <VersionChipInner />;
}

function VersionChipInner() {
  const { t } = useTranslation('common');
  const { data } = useBuildVersion();

  // No manifest means a build that was never packaged into the image (`vite
  // dev` answers /version.json with the SPA shell). Nothing useful to name.
  if (!data) return null;

  // `dev` is the Dockerfile default, i.e. an image built outside the deploy
  // workflow. Shown as-is rather than dressed up as `vdev`, because "this is
  // not a released build" is exactly the signal an admin wants locally.
  const label = data.release === 'dev' ? data.release : `v${data.release}`;

  return (
    <span
      className="ml-2 select-none text-[11px] tabular-nums text-muted-foreground"
      title={t('version.tooltip', {
        release: data.release,
        commit: data.commit.slice(0, SHORT_COMMIT_LENGTH),
      })}
      aria-label={t('version.label')}
    >
      {label}
    </span>
  );
}
